import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Collapse, LinearProgress, CircularProgress, Alert, ToggleButton, ToggleButtonGroup, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel } from '@mui/material'
import React, { useEffect, useState, useRef } from 'react'
import { useSessionContext } from 'session'
import { startETLJob, startPhase2ETLJob, cancelETLJob, listETLJobs, getETLJobStatus, getETLSteps, listAristotleJobs, isKWSyncGateMetrics, KW_SYNC_SOURCE, type Phase2Selection, type ETLJobListItem, type ETLJob, type ETLStep, type JobType, type AristotleJob } from 'services'
import { MatchRatePanel } from 'components/us/pidf/match-rate-panel'
import { KWSyncGatePanel } from 'components/us/pidf/kw-sync-gate-panel'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import RefreshIcon from '@mui/icons-material/Refresh'

type JobStatus = 'pending' | 'running' | 'completed' | 'failed' | 'canceled'
type ChipColor = 'default' | 'primary' | 'success' | 'error' | 'warning'

const STATUS_COLORS: Record<JobStatus, ChipColor> = {
  pending: 'default',
  running: 'primary',
  completed: 'success',
  failed: 'error',
  canceled: 'warning'
}

const STEP_COLORS = {
  completed: '#4caf50',
  current: '#2196f3',
  pending: '#e0e0e0'
}

const JOB_TYPE_LABELS: Record<JobType, string> = {
  pidf: 'PIDF',
  pidf_2: 'PIDF Phase 2',
  lobbylinx: 'LobbyLinx',
  nyne: 'Nyne Enrichment',
  hep: 'HEP',
  kw_sync: 'KW Sync'
}

const DEFAULT_SOURCE_LABELS: Partial<Record<JobType, string>> = {
  pidf: 'KnowWho data list',
  nyne: 'Phase 1 staged rows'
}

export const PIDFPage: React.FC = () => {
  const { changeModuleName, changeUniqueId } = useSessionContext()
  const [jobs, setJobs] = useState<ETLJobListItem[]>([])
  const [stepsByJobType, setStepsByJobType] = useState<Record<JobType, ETLStep[]>>({
    pidf: [],
    pidf_2: [],
    lobbylinx: [],
    nyne: [],
    hep: [],
    kw_sync: []
  })
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null)
  const [jobStatuses, setJobStatuses] = useState<Record<number, ETLJob>>({})
  const [loadingStatuses, setLoadingStatuses] = useState<Set<number>>(new Set())
  const [startingJob, setStartingJob] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedJobType, setSelectedJobType] = useState<JobType>('pidf')
  const fetchingJobsRef = useRef(false)
  const fetchingStatusRef = useRef<Set<number>>(new Set())
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const [phase2DialogOpen, setPhase2DialogOpen] = useState(false)
  const [aristotleJobs, setAristotleJobs] = useState<AristotleJob[]>([])
  const [kwSyncSteps, setKwSyncSteps] = useState<ETLStep[]>([])
  const [kwSyncLoadSteps, setKwSyncLoadSteps] = useState<ETLStep[]>([])
  const [kwSyncAristotleSteps, setKwSyncAristotleSteps] = useState<ETLStep[]>([])
  const [startSource, setStartSource] = useState<'' | typeof KW_SYNC_SOURCE>('')
  const [phase2Selection, setPhase2Selection] = useState<Phase2Selection | null>(null)
  const [loadingAristotleJobs, setLoadingAristotleJobs] = useState(true)

  useEffect(() => {
    changeModuleName('Policy Influencer Data File ETL Management')
    changeUniqueId('')
  }, [changeModuleName])

  const fetchJobs = async (showRefreshing: boolean = false): Promise<void> => {
    // Prevent concurrent requests
    if (fetchingJobsRef.current) {
      return
    }

    fetchingJobsRef.current = true
    if (showRefreshing) setRefreshing(true)

    try {
      setJobs(await listETLJobs(20, selectedJobType))
      setError(null)
    } catch (err) {
      setError('Failed to load ETL jobs')
    } finally {
      setLoading(false)
      setRefreshing(false)
      fetchingJobsRef.current = false
    }
  }

  const fetchSteps = async (jobType: JobType): Promise<void> => {
    try {
      if (jobType === 'pidf') {
        const [steps, loadSteps] = await Promise.all([getETLSteps(jobType), getETLSteps(jobType, KW_SYNC_SOURCE)])
        setStepsByJobType(prev => ({ ...prev, [jobType]: steps }))
        setKwSyncLoadSteps(loadSteps)
        return
      }
      if (jobType === 'pidf_2') {
        const [steps, syncSteps] = await Promise.all([getETLSteps(jobType), getETLSteps(jobType, KW_SYNC_SOURCE)])
        setStepsByJobType(prev => ({ ...prev, [jobType]: steps }))
        setKwSyncSteps(syncSteps)
        return
      }
      const steps = await getETLSteps(jobType)
      setStepsByJobType(prev => ({ ...prev, [jobType]: steps }))
    } catch (err) {
      console.error('Failed to load ETL steps:', err)
    }
  }

  const fetchAristotleJobs = async (): Promise<void> => {
    setLoadingAristotleJobs(true)
    try {
      const jobs = await listAristotleJobs(50)
      setAristotleJobs(jobs)
      setError(null)
    } catch (err) {
      setError('Failed to load Aristotle jobs')
    } finally {
      setLoadingAristotleJobs(false)
    }
  }

  const fetchJobStatus = async (jobId: number): Promise<void> => {
    // Prevent concurrent requests for the same job
    if (fetchingStatusRef.current.has(jobId)) {
      return
    }

    fetchingStatusRef.current.add(jobId)
    setLoadingStatuses(prev => new Set(prev).add(jobId))
    try {
      const status = await getETLJobStatus(jobId)
      setJobStatuses(prev => ({ ...prev, [jobId]: status }))
    } catch (err) {
      // Silently fail
    } finally {
      fetchingStatusRef.current.delete(jobId)
      setLoadingStatuses(prev => {
        const next = new Set(prev)
        next.delete(jobId)
        return next
      })
    }
  }

  useEffect(() => {
    void fetchJobs()
    void fetchSteps(selectedJobType)
  }, [])

  useEffect(() => {
    fetchingJobsRef.current = false
    setLoading(true)
    void fetchSteps(selectedJobType)
    void fetchJobs()
  }, [selectedJobType])

  useEffect(() => {
    const hasRunningJobs = jobs.some(job => job.status === 'running' || job.status === 'pending')

    // Clear existing interval if any
    if (pollingIntervalRef.current !== null) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    // Only start polling if there are running jobs
    if (!hasRunningJobs) return

    // Start new polling interval
    pollingIntervalRef.current = setInterval(() => {
      const runningJobs = jobs.filter(job => job.status === 'running' || job.status === 'pending')
      runningJobs.forEach(job => { void fetchJobStatus(job.id) })
      void fetchJobs()
    }, 3000)

    return () => {
      if (pollingIntervalRef.current !== null) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [jobs])

  useEffect(() => {
    if (expandedJobId != null) void fetchJobStatus(expandedJobId)
  }, [expandedJobId])

  const handleStartJob = async (): Promise<void> => {
    setStartingJob(true)
    setError(null)
    try {
      await startETLJob(selectedJobType, startSource === '' ? undefined : startSource)
      await fetchJobs()
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to start ETL job')
    } finally {
      setStartingJob(false)
    }
  }

  const handleStartPhase2 = (): void => {
    void fetchAristotleJobs()
    setPhase2Selection(null)
    setPhase2DialogOpen(true)
  }

  const handlePhase2Submit = async (): Promise<void> => {
    if (phase2Selection === null) {
      setError('Please select an Aristotle job or KW Sync')
      return
    }

    setStartingJob(true)
    setError(null)
    setPhase2DialogOpen(false)
    try {
      await startPhase2ETLJob(phase2Selection)
      await fetchJobs()
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to start Phase 2 ETL job')
    } finally {
      setStartingJob(false)
    }
  }

  const handleCancelJob = async (jobId: number): Promise<void> => {
    try {
      await cancelETLJob(jobId)
      await fetchJobs()
      setError(null)
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to cancel ETL job')
    }
  }

  // Fetch Aristotle jobs on mount to check for unimported jobs
  useEffect(() => {
    void fetchAristotleJobs()
  }, [])

  const kwSyncAristotleJobId = aristotleJobs.find(job => job.source === KW_SYNC_SOURCE)?.id
  useEffect(() => {
    if (kwSyncAristotleJobId === undefined) return
    getETLSteps('pidf_2', undefined, kwSyncAristotleJobId).then(setKwSyncAristotleSteps).catch((err) => { console.error('Failed to load ETL steps:', err) })
  }, [kwSyncAristotleJobId])

  const hasRunningJob = jobs.some(job => job.status === 'running' && job.job_type === selectedJobType)
  const filteredJobs = jobs.filter(job => job.job_type === selectedJobType)

  const importableAristotleJobs = aristotleJobs.filter((job: AristotleJob) => job.table_name_voters != null && !job.imported)
  // Check if there are any unimported Aristotle jobs
  const hasUnimportedAristotleJobs = aristotleJobs.some((job: AristotleJob) => !job.imported)
  // Phase 1 should be blocked if there are unimported jobs (need to process them first)
  // Phase 2 should be allowed if there are unimported jobs (to process them)
  // Both buttons should be disabled while loading Aristotle jobs or if a job is running
  const canStartPhase1 = !loadingAristotleJobs && !hasUnimportedAristotleJobs && !hasRunningJob
  const canStartPhase2 = !loadingAristotleJobs && !hasRunningJob

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
      <Box width='95%'>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">ETL Jobs</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <ToggleButtonGroup
              value={selectedJobType}
              exclusive
              onChange={(_, newType) => { if (newType !== null) { setSelectedJobType(newType); setStartSource('') } }}
              size="small"
            >
              <ToggleButton value="pidf">PIDF</ToggleButton>
              <ToggleButton value="pidf_2">PIDF Phase 2</ToggleButton>
              <ToggleButton value="lobbylinx">LobbyLinx</ToggleButton>
              <ToggleButton value="nyne">Nyne</ToggleButton>
              <ToggleButton value="hep">HEP</ToggleButton>
              <ToggleButton value="kw_sync">KW Sync</ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => { void fetchJobs(true) }}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            {DEFAULT_SOURCE_LABELS[selectedJobType] !== undefined && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="start-source-select-label">Source</InputLabel>
                <Select
                  labelId="start-source-select-label"
                  value={startSource}
                  onChange={(e) => { setStartSource(e.target.value as '' | typeof KW_SYNC_SOURCE) }}
                  label="Source"
                >
                  <MenuItem value="">{DEFAULT_SOURCE_LABELS[selectedJobType]}</MenuItem>
                  <MenuItem value={KW_SYNC_SOURCE}>KW Sync — aristotle route</MenuItem>
                </Select>
              </FormControl>
            )}
            {selectedJobType === 'pidf' && (
              <Button variant="contained" color="primary" startIcon={startingJob ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                      onClick={() => { void handleStartJob() }} disabled={startingJob || !canStartPhase1}
                      title={hasUnimportedAristotleJobs ? 'Cannot start Phase 1: There are unimported Aristotle jobs. Please run Phase 2 first.' : ''}>
                {startingJob ? 'Starting...' : 'Start Phase 1'}
              </Button>
            )}
            {selectedJobType === 'pidf_2' && (
              <Button variant="contained" color="secondary" startIcon={startingJob ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                      onClick={handleStartPhase2} disabled={startingJob || !canStartPhase2}>
                {startingJob ? 'Starting...' : 'Start Phase 2'}
              </Button>
            )}
            {(selectedJobType === 'lobbylinx' || selectedJobType === 'hep' || selectedJobType === 'nyne' || selectedJobType === 'kw_sync') && (
              <Button variant="contained" color="primary" startIcon={startingJob ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                      onClick={() => { void handleStartJob() }} disabled={startingJob || hasRunningJob}>
                {startingJob ? 'Starting...' : selectedJobType === 'kw_sync' ? 'Start New KW Sync Job' : `Start New ${JOB_TYPE_LABELS[selectedJobType]} ETL Job`}
              </Button>
            )}
          </Box>
        </Box>

        {error != null && <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setError(null) }}>{error}</Alert>}

        {loading
          ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            )
          : (
          <ETLJobsTable
            jobs={filteredJobs}
            stepsByJobType={stepsByJobType}
            kwSyncSteps={kwSyncSteps}
            kwSyncLoadSteps={kwSyncLoadSteps}
            kwSyncAristotleSteps={kwSyncAristotleSteps}
            expandedJobId={expandedJobId}
            jobStatuses={jobStatuses}
            loadingStatuses={loadingStatuses}
            onToggleExpand={(id) => { setExpandedJobId(expandedJobId === id ? null : id) }}
            onCancelJob={(id) => { void handleCancelJob(id) } }
          />
            )}
      </Box>

      <Dialog open={phase2DialogOpen} onClose={() => { setPhase2DialogOpen(false) }} maxWidth="sm" fullWidth>
        <DialogTitle>Start Phase 2 ETL Job</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select an Aristotle job to process (Phase 2 uses its append tables), or KW Sync to promote the staged sync corrections without Aristotle.
          </Typography>
          {loadingAristotleJobs
            ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={30} />
            </Box>
              )
            : (
            <FormControl fullWidth margin="dense">
              <InputLabel id="aristotle-job-select-label">Source</InputLabel>
              <Select
                labelId="aristotle-job-select-label"
                value={phase2Selection ?? ''}
                onChange={(e) => { setPhase2Selection(e.target.value as Phase2Selection) }}
                label="Source"
              >
                <MenuItem value={KW_SYNC_SOURCE}>
                  <Box>
                    <Typography variant="body2">KW Sync (staged rows)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Promote the pending KW Sync corrections — no Aristotle submission
                    </Typography>
                  </Box>
                </MenuItem>
                {importableAristotleJobs.map((job: AristotleJob) => (
                  <MenuItem key={job.id} value={job.id}>
                    <Box>
                      <Typography variant="body2">
                        {job.file_name} (ID: {job.id}){job.source === KW_SYNC_SOURCE && ' — KW Sync aristotle route'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Created: {new Date(job.created_at).toLocaleString()} |
                        Voter: {job.voter_match_status} |
                        {job.imported ? ' ✓ Imported' : ' ⚠ Not imported'}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
              )}
          {importableAristotleJobs.length === 0 && !loadingAristotleJobs && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No Aristotle jobs with table names found. Please run an Aristotle match first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPhase2DialogOpen(false) }}>Cancel</Button>
          <Button
            onClick={() => { void handlePhase2Submit() }}
            variant="contained"
            color="primary"
            disabled={phase2Selection === null}
          >
            Start Phase 2
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

interface ETLJobsTableProps {
  jobs: ETLJobListItem[]
  stepsByJobType: Record<JobType, ETLStep[]>
  kwSyncSteps: ETLStep[]
  kwSyncLoadSteps: ETLStep[]
  kwSyncAristotleSteps: ETLStep[]
  expandedJobId: number | null
  jobStatuses: Record<number, ETLJob>
  loadingStatuses: Set<number>
  onToggleExpand: (jobId: number) => void
  onCancelJob: (jobId: number) => void
}

const formatDate = (dateString: string | null): string => dateString !== null ? new Date(dateString).toLocaleString() : '-'

type JobDetailTab = 'steps' | 'match'

const ETLJobsTable: React.FC<ETLJobsTableProps> = ({ jobs, stepsByJobType, kwSyncSteps, kwSyncLoadSteps, kwSyncAristotleSteps, expandedJobId, jobStatuses, loadingStatuses, onToggleExpand, onCancelJob }) => {
  const [activeTab, setActiveTab] = useState<JobDetailTab>('steps')

  const jobSteps = (jobType: JobType, message: string): ETLStep[] => {
    const defaultSteps = stepsByJobType[jobType] ?? []
    const laneSteps = jobType === 'pidf' && message.includes('KW Sync (aristotle route)')
      ? kwSyncLoadSteps
      : message.includes('KW Sync Aristotle job')
        ? kwSyncAristotleSteps
        : message.includes('KW Sync staged rows') ? kwSyncSteps : defaultSteps
    return laneSteps.length > 0 ? laneSteps : defaultSteps
  }

  // Reset the active tab to "steps" whenever a different job is expanded.
  // Otherwise, expanding a job without metrics after viewing the Match Rate
  // tab on a different job would render an empty area (the tab is hidden but
  // the state still holds 'match').
  useEffect(() => {
    setActiveTab('steps')
  }, [expandedJobId])

  if (jobs.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">No ETL jobs found. Start a new job to begin.</Typography>
      </Paper>
    )
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#efefef' }}>
            <TableCell width={50} />
            <TableCell><strong>ID</strong></TableCell>
            <TableCell><strong>Type</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell><strong>Progress</strong></TableCell>
            <TableCell><strong>Latest Message</strong></TableCell>
            <TableCell><strong>Created</strong></TableCell>
            <TableCell><strong>Started</strong></TableCell>
            <TableCell><strong>Finished</strong></TableCell>
            <TableCell><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.map((job) => (
            <React.Fragment key={job.id}>
              <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                  <IconButton size="small" onClick={() => { onToggleExpand(job.id) }}>
                    {expandedJobId === job.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </TableCell>
                <TableCell>{job.id}</TableCell>
                <TableCell>
                  <Chip label={JOB_TYPE_LABELS[job.job_type]}
                  color={job.job_type === 'pidf_2' ? 'secondary' : 'default'}
                  size="small"
                  variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip label={job.status.toUpperCase()} color={STATUS_COLORS[job.status]} size="small" />
                  {job.is_stalled && <Chip label="NO HEARTBEAT" color="warning" size="small" sx={{ ml: 0.5 }} />}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{job.current_step}/{job.total_steps}</Typography>
                    <Box sx={{ width: 100 }}>
                      <LinearProgress variant="determinate" value={job.status === 'completed' ? 100 : ((job.current_step - 1) / job.total_steps) * 100} />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                    {job.latest_message ?? '-'}
                  </Typography>
                </TableCell>
                <TableCell>{formatDate(job.created_at)}</TableCell>
                <TableCell>{formatDate(job.started_at)}</TableCell>
                <TableCell>{formatDate(job.finished_at)}</TableCell>
                <TableCell>
                  {job.status === 'running' && (
                    <Button size="small" variant="outlined" color="error" startIcon={<StopIcon />}
                            onClick={() => { onCancelJob(job.id) }}>Cancel</Button>
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                  <Collapse in={expandedJobId === job.id} timeout="auto" unmountOnExit>
                    <ExpandedJobDetail
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                      steps={jobSteps(job.job_type, jobStatuses[job.id]?.message ?? '')}
                      job={job}
                      jobStatus={jobStatuses[job.id]}
                      isLoadingStatus={loadingStatuses.has(job.id)}
                    />
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

interface ExpandedJobDetailProps {
  activeTab: JobDetailTab
  onTabChange: (tab: JobDetailTab) => void
  steps: ETLStep[]
  job: ETLJobListItem
  jobStatus?: ETLJob
  isLoadingStatus: boolean
}

const ExpandedJobDetail: React.FC<ExpandedJobDetailProps> = ({ activeTab, onTabChange, steps, job, jobStatus, isLoadingStatus }) => {
  // Show the Match Rate tab only when the backend has populated metrics.
  // Other job types and Phase 2 jobs without a metrics backfill render
  // exactly as before — no tab bar at all.
  const metrics = jobStatus?.metrics ?? null
  const showMatchTab = metrics !== null && metrics !== undefined
  const isGateMetrics = showMatchTab && isKWSyncGateMetrics(metrics)

  if (!showMatchTab) {
    return (
      <Box sx={{ margin: 2 }}>
        <Typography variant="h6" gutterBottom>Job Steps</Typography>
        <ETLJobSteps
          steps={steps}
          job={job}
          jobStatus={jobStatus}
          isLoadingStatus={isLoadingStatus}
        />
      </Box>
    )
  }

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(_, v: JobDetailTab) => { onTabChange(v) }}
        sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 3 }}
      >
        <Tab value="steps" label="Job Steps" />
        <Tab value="match" label={isGateMetrics ? 'Orphan Scan' : 'Match Rate'} />
      </Tabs>
      {activeTab === 'steps'
        ? (
        <Box sx={{ margin: 2 }}>
          <ETLJobSteps
            steps={steps}
            job={job}
            jobStatus={jobStatus}
            isLoadingStatus={isLoadingStatus}
          />
        </Box>
          )
        : isKWSyncGateMetrics(metrics)
          ? (
        <KWSyncGatePanel jobId={job.id} completed={(jobStatus?.status ?? job.status) === 'completed'} metrics={metrics} />
            )
          : (
        <MatchRatePanel metrics={metrics} />
            )}
    </Box>
  )
}

interface ETLJobStepsProps {
  steps: ETLStep[]
  job: ETLJobListItem
  jobStatus?: ETLJob
  isLoadingStatus: boolean
}

const ETLJobSteps: React.FC<ETLJobStepsProps> = ({ steps, job, jobStatus, isLoadingStatus }) => {
  const status = jobStatus?.status ?? job.status
  const currentStep = jobStatus?.current_step ?? job.current_step
  const isJobFinished = ['completed', 'failed', 'canceled'].includes(status)
  const messageBoxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (messageBoxRef.current != null && jobStatus?.message != null && jobStatus.message.length > 0) {
      messageBoxRef.current.scrollTop = messageBoxRef.current.scrollHeight
    }
  }, [jobStatus?.message])

  const getStepStatus = (stepNumber: number): 'completed' | 'current' | 'pending' => {
    if (isJobFinished && stepNumber <= currentStep) return 'completed'
    if (stepNumber < currentStep) return 'completed'
    if (stepNumber === currentStep && !isJobFinished) return 'current'
    return 'pending'
  }

  return (
    <Box>
      {((jobStatus?.error_message) != null) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Error:</strong> {jobStatus.error_message}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {steps.map((step) => {
          const stepStatus = getStepStatus(step.number)
          const isCurrent = stepStatus === 'current'
          return (
            <Box key={step.number} sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 1,
              borderRadius: 1,
              backgroundColor: isCurrent ? '#e3f2fd' : 'transparent'
            }}>
              <Box sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: STEP_COLORS[stepStatus],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: stepStatus === 'pending' ? '#666' : 'white',
                fontWeight: 'bold'
              }}>
                {step.number}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={isCurrent ? 'bold' : 'normal'}>
                  {step.name} - {step.description}
                  {stepStatus === 'completed' && ' ✓'}
                </Typography>
                {isCurrent && status === 'running' && <LinearProgress sx={{ mt: 0.5, height: 4, borderRadius: 2 }} />}
              </Box>
            </Box>
          )
        })}
      </Box>
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        {isLoadingStatus && (jobStatus == null)
          ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Loading job details...</Typography>
          </Box>
            )
          : (
          <>
            <Typography variant="body2" color="text.secondary">
              <strong>Progress:</strong> {isJobFinished ? 100 : Math.round(((currentStep - 1) / job.total_steps) * 100)}% complete
            </Typography>
            {(jobStatus?.heartbeat_at != null) && (
              <Typography variant="body2" color="text.secondary">
                <strong>Last heartbeat:</strong> {new Date(jobStatus.heartbeat_at).toLocaleString()}
              </Typography>
            )}
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary"><strong>Messages:</strong></Typography>
              <Box ref={messageBoxRef} sx={{ mt: 0.5, p: 1, backgroundColor: '#ffffff', borderRadius: 1, border: '1px solid #e0e0e0', maxHeight: 200, overflowY: 'auto' }}>
                <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                  {jobStatus?.message ?? job.latest_message ?? 'No messages yet'}
                </Typography>
              </Box>
            </Box>
          </>
            )}
      </Box>
    </Box>
  )
}
